import { useState, useEffect, useCallback, useMemo } from 'react'
import { useApi } from '@/hooks/useApi'
import type { ProyectoCorreo } from '@/lib/types'
import { Button } from '@/components/ui'
import BuscarCorreoModal from './BuscarCorreoModal'
import NuevoCorreoModal from './NuevoCorreoModal'
import CorreoListaItem from './CorreoListaItem'
import CorreoHilo from './CorreoHilo'
import { IconMail, IconPlus, IconSearch, IconSend } from '@/components/icons/Icons'
import { etiquetaHilo, participantesExternos } from '@/lib/correo-utils'

/*
  Bandeja de correos de una ficha: lista a un lado, hilo al otro.

  El listado no trae los cuerpos (pesan de más); el hilo se pide al abrirlo y
  queda cacheado en `detalle` para que volver a él sea instantáneo.
*/
interface Props {
  basePath: string
  /** Cómo se llama la ficha en los textos: «proyecto», «oportunidad». */
  nombreFicha: string
  /** Para el contador de la solapa: se llama en cada carga. */
  onCount?: (n: number) => void
}

export default function PanelCorreos({ basePath, nombreFicha, onCount }: Props) {
  const api = useApi()
  const [correos, setCorreos] = useState<ProyectoCorreo[]>([])
  const [loading, setLoading] = useState(true)
  const [abierto, setAbierto] = useState<number | null>(null)
  const [detalle, setDetalle] = useState<Record<number, ProyectoCorreo>>({})
  const [showBuscar, setShowBuscar] = useState(false)
  const [showNuevo, setShowNuevo] = useState(false)
  const [ocupado, setOcupado] = useState<number | null>(null)
  const [filtro, setFiltro] = useState('')

  // `api` no entra en las dependencias: useApi() devuelve un objeto nuevo en
  // cada render, así que incluirlo recreaba `cargar`, volvía a disparar el
  // efecto y dejaba la vista recargando en bucle. Mismo criterio que el resto
  // de las pantallas del ERP.
  const cargar = useCallback(async () => {
    setLoading(true)
    try {
      const filas = await api.get<ProyectoCorreo[]>(basePath)
      setCorreos(filas)
      onCount?.(filas.length)
    } catch (err) {
      console.error('Error cargando correos:', err)
    } finally {
      setLoading(false)
    }
  }, [basePath]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { cargar() }, [cargar])

  const visibles = useMemo(() => {
    const q = filtro.trim().toLowerCase()
    if (!q) return correos
    return correos.filter(c =>
      (c.asunto || '').toLowerCase().includes(q) ||
      (c.participantes || []).some(p => p.toLowerCase().includes(q))
    )
  }, [correos, filtro])

  async function abrir(c: ProyectoCorreo) {
    setAbierto(c.id)
    if (detalle[c.id]) return
    try {
      const full = await api.get<ProyectoCorreo>(`${basePath}/${c.id}`)
      setDetalle(prev => ({ ...prev, [c.id]: full }))
    } catch (err) {
      console.error('Error abriendo el hilo:', err)
    }
  }

  async function actualizar(c: ProyectoCorreo) {
    setOcupado(c.id)
    try {
      await api.post(basePath, { gmail_thread_id: c.gmail_thread_id })
      setDetalle(prev => { const n = { ...prev }; delete n[c.id]; return n })
      await cargar()
      const full = await api.get<ProyectoCorreo>(`${basePath}/${c.id}`)
      setDetalle(prev => ({ ...prev, [c.id]: full }))
    } catch (err) {
      console.error('Error actualizando el hilo:', err)
    } finally {
      setOcupado(null)
    }
  }

  async function quitar(c: ProyectoCorreo) {
    // El correo sigue en Gmail: esto solo lo saca de la ficha.
    if (!confirm(`¿Quitar "${c.asunto}" de esta ${nombreFicha}?\n\nEl correo no se borra de Gmail.`)) return
    setOcupado(c.id)
    try {
      await api.del(`${basePath}/${c.id}`)
      setAbierto(null)
      await cargar()
    } catch (err) {
      console.error('Error quitando el correo:', err)
    } finally {
      setOcupado(null)
    }
  }

  if (loading) return <p className="text-muted">Cargando correos…</p>

  if (correos.length === 0) {
    return (
      <div className="proyecto-correos">
        <div className="card" style={{ padding: 28, textAlign: 'center' }}>
          <IconMail size={28} />
          <h3 style={{ marginTop: 10 }}>Todavía no hay correos en esta {nombreFicha}</h3>
          <p className="text-muted" style={{ marginTop: 6, marginBottom: 16 }}>
            Buscá un hilo en Gmail y archivalo acá: queda el texto de los mensajes y un enlace al original.
          </p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
            <Button onClick={() => setShowNuevo(true)}><IconSend size={14} /> Escribir</Button>
            <button className="btn btn-secondary" onClick={() => setShowBuscar(true)}>
              <IconPlus size={14} /> Archivar uno existente
            </button>
          </div>
        </div>
        <BuscarCorreoModal
          basePath={basePath}
          nombreFicha={nombreFicha}
          open={showBuscar}
          onClose={() => setShowBuscar(false)}
          onArchivado={cargar}
        />
        <NuevoCorreoModal
          basePath={basePath}
          nombreFicha={nombreFicha}
          open={showNuevo}
          onClose={() => setShowNuevo(false)}
          onEnviado={cargar}
        />
      </div>
    )
  }

  const activo = correos.find(c => c.id === abierto) || null

  return (
    <div className="proyecto-correos">
      <div className="filters-bar">
        <div className="filters-bar__search">
          <IconSearch size={14} className="filters-bar__search-icon" />
          <input
            type="text"
            className="form-input"
            placeholder="Buscar en los correos archivados…"
            value={filtro}
            onChange={e => setFiltro(e.target.value)}
          />
        </div>
        <span className="text-muted" style={{ fontSize: 13 }}>
          {visibles.length === correos.length
            ? `${correos.length} ${correos.length === 1 ? 'hilo' : 'hilos'}`
            : `${visibles.length} de ${correos.length}`}
        </span>
        <div className="filters-bar__clear" style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary btn-sm" onClick={() => setShowBuscar(true)}>
            <IconPlus size={14} /> Archivar
          </button>
          <Button onClick={() => setShowNuevo(true)}><IconSend size={14} /> Escribir</Button>
        </div>
      </div>

      <div className={`correos-shell${activo ? ' is-leyendo' : ''}`}>
        <div className="correos-lista">
          {visibles.length === 0 ? (
            <p className="text-muted" style={{ padding: 16, fontSize: 14 }}>
              Ningún correo archivado coincide con «{filtro}».
            </p>
          ) : visibles.map(c => {
            // Quien archivo el hilo es siempre alguien de la casa, asi que de
            // ahi sale cual es "nuestro" dominio. Se prefiere a useAuth(): ese
            // hook mantiene estado por instancia, o sea un /auth/me extra por
            // montar la pestana y un primer render sin usuario.
            const externos = participantesExternos(c.participantes, c.archivado_por)
            return (
              <CorreoListaItem
                key={c.id}
                quien={etiquetaHilo(c.participantes, c.de_ultimo, c.archivado_por)}
                avatarDe={externos[0] || ''}
                asunto={c.asunto}
                fecha={c.fecha_ultimo}
                adjuntos={c.adjuntos?.length ?? 0}
                mensajes={c.cantidad}
                activo={c.id === abierto}
                onClick={() => abrir(c)}
              />
            )
          })}
        </div>

        {activo ? (
          <CorreoHilo
            basePath={basePath}
          nombreFicha={nombreFicha}
            resumen={activo}
            detalle={detalle[activo.id] || null}
            ocupado={ocupado === activo.id}
            onActualizar={() => actualizar(activo)}
            onQuitar={() => quitar(activo)}
            onVolver={() => setAbierto(null)}
            onRespondido={() => actualizar(activo)}
          />
        ) : (
          <div className="correos-hilo">
            <div className="correos-vacio">
              <IconMail size={26} />
              <p>Elegí un hilo de la izquierda para leerlo.</p>
            </div>
          </div>
        )}
      </div>

      <BuscarCorreoModal
        basePath={basePath}
          nombreFicha={nombreFicha}
        open={showBuscar}
        onClose={() => setShowBuscar(false)}
        onArchivado={cargar}
      />

      <NuevoCorreoModal
        basePath={basePath}
          nombreFicha={nombreFicha}
        open={showNuevo}
        onClose={() => setShowNuevo(false)}
        onEnviado={cargar}
      />
    </div>
  )
}
